import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, Upload, Package, Receipt, GripVertical, X } from "lucide-react";
import { fetchProducts, adminMe, createProduct, updateProduct, deleteProduct, reorderProducts, uploadImage, fetchOrders, fetchNextOrderNumber, getSettings, updateSettings } from "@/api";
import { CURRENCIES, formatPrice } from "@/i18n";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CATEGORIES = ["clothing", "bags", "shoes", "jewellery"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
// EU shoe sizes 35–47 including every half-size, using the ½ symbol.
const SHOE_SIZES = (() => {
  const out = [];
  for (let n = 35; n <= 47; n += 0.5) out.push(Number.isInteger(n) ? String(n) : `${Math.floor(n)}½`);
  return out;
})();
// Russian ring sizes = inner diameter in mm (15–22.5 in 0.5 steps)
const RING_SIZES = ["15", "15.5", "16", "16.5", "17", "17.5", "18", "18.5", "19", "19.5", "20", "20.5", "21", "21.5", "22", "22.5"];
const sizeListFor = (cat) => (cat === "shoes" ? SHOE_SIZES : cat === "jewellery" ? RING_SIZES : SIZES);
const sizePrefix = (cat) => (cat === "shoes" ? "EU " : cat === "jewellery" ? "RU " : "");
const COLORS = [
  { name: "Black", hex: "#111111" },
  { name: "White", hex: "#F5F5F0" },
  { name: "Gold", hex: "#C4A24A" },
  { name: "Silver", hex: "#C0C0C0" },
  { name: "Beige", hex: "#D8C5A5" },
  { name: "Brown", hex: "#6B4A2B" },
  { name: "Red", hex: "#8E1B1B" },
  { name: "Blue", hex: "#1E3A5F" },
  { name: "Green", hex: "#2F4F3E" },
  { name: "Pink", hex: "#E6B7C1" },
];
const EMPTY = { name: "", brand: "Atelier Heda", category: "jewellery", description: "", price_rub: "", original_price_rub: "", image: "", images: [], featured: false, colors: [], stock: "", sizes: [], size_stock: {} };

export default function Admin() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [priceCurrency, setPriceCurrency] = useState("USD");
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("products");
  const [orders, setOrders] = useState([]);
  const [nextOrderNo, setNextOrderNo] = useState(null);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [savingTax, setSavingTax] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [imgDrag, setImgDrag] = useState(null);
  const [imgOver, setImgOver] = useState(null);
  const [ringSizing, setRingSizing] = useState(false);
  const [listCategory, setListCategory] = useState("all");

  const load = useCallback(() => {
    fetchProducts().then(setProducts).catch(() => {});
    fetchOrders().then(setOrders).catch(() => {});
    fetchNextOrderNumber().then((d) => setNextOrderNo(d.next_order_number)).catch(() => {});
    getSettings().then((s) => setTaxEnabled(!!s.tax_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("hg_admin_token")) {
      navigate("/admin/login");
      return;
    }
    adminMe()
      .then(() => { setReady(true); load(); })
      .catch(() => {
        localStorage.removeItem("hg_admin_token");
        navigate("/admin/login");
      });
  }, [navigate, load]);

  const logout = () => {
    localStorage.removeItem("hg_admin_token");
    navigate("/admin/login");
  };

  const update = (k) => (e) => {
    const v = k === "featured" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    const sizeStock = p.size_stock && Object.keys(p.size_stock).length
      ? { ...p.size_stock }
      : (p.sizes || []).reduce((acc, s) => ({ ...acc, [s]: 1 }), {});
    const usdAmount = Math.round((p.price_rub * CURRENCIES.USD.rate) * 100) / 100;
    const usdOriginal = p.original_price_rub ? Math.round((p.original_price_rub * CURRENCIES.USD.rate) * 100) / 100 : "";
    setForm({ ...p, price_rub: usdAmount, original_price_rub: usdOriginal, description: p.subtitle_en || p.subtitle_ru || "", colors: p.colors || [], stock: p.stock ?? "", sizes: p.sizes || [], size_stock: sizeStock, images: (p.images && p.images.length ? p.images : (p.image ? [p.image] : [])) });
    setRingSizing(p.category === "jewellery" && (p.sizes || []).length > 0);
    setPriceCurrency("USD");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => { setEditingId(null); setForm(EMPTY); setPriceCurrency("USD"); setRingSizing(false); };

  const toggleTax = async () => {
    const next = !taxEnabled;
    setSavingTax(true);
    setTaxEnabled(next);
    try {
      await updateSettings({ tax_enabled: next });
      toast.success(next ? "Automatic tax enabled" : "Automatic tax disabled");
    } catch (err) {
      setTaxEnabled(!next);
      toast.error("Could not update tax setting");
      if (err.response?.status === 401) logout();
    } finally {
      setSavingTax(false);
    }
  };

  const fmtUsd = (cents) => `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  const toggleColor = (name) => {
    setForm((f) => ({
      ...f,
      colors: f.colors.includes(name) ? f.colors.filter((c) => c !== name) : [...f.colors, name],
    }));
  };

  const updateSizeStock = (size) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, size_stock: { ...f.size_stock, [size]: v } }));
  };

  const addSize = (size) => {
    if (!size) return;
    setForm((f) => (f.size_stock?.[size] != null ? f : { ...f, size_stock: { ...f.size_stock, [size]: "1" } }));
  };

  const removeSize = (size) => {
    setForm((f) => {
      const next = { ...(f.size_stock || {}) };
      delete next[size];
      return { ...f, size_stock: next };
    });
  };

  const changeCategory = (e) => {
    const category = e.target.value;
    setRingSizing(false);
    setForm((f) => ({ ...f, category, sizes: [], size_stock: {} }));
  };

  const toggleRingSizing = () => {
    setRingSizing((v) => !v);
    setForm((f) => ({ ...f, sizes: [], size_stock: {}, stock: "" }));
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 5 - (form.images?.length || 0);
    if (room <= 0) { toast.error("Up to 5 photos per item"); return; }
    setUploading(true);
    try {
      const toUpload = files.slice(0, room);
      const urls = [];
      for (const file of toUpload) urls.push(await uploadImage(file));
      setForm((f) => {
        const images = [...(f.images || []), ...urls].slice(0, 5);
        return { ...f, images, image: images[0] || f.image };
      });
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error("Image upload failed");
      if (err.response?.status === 401) logout();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addImageUrl = () => {
    const url = (form.image || "").trim();
    if (!url) return;
    setForm((f) => {
      if ((f.images || []).includes(url) || (f.images || []).length >= 5) return f;
      const images = [...(f.images || []), url].slice(0, 5);
      return { ...f, images, image: images[0] };
    });
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const images = (f.images || []).filter((_, i) => i !== idx);
      return { ...f, images, image: images[0] || "" };
    });
  };

  const reorderImage = (from, to) => {
    if (from == null || to == null || from === to) return;
    setForm((f) => {
      const images = [...(f.images || [])];
      if (from < 0 || from >= images.length || to < 0 || to >= images.length) return f;
      const [moved] = images.splice(from, 1);
      images.splice(to, 0, moved);
      return { ...f, images, image: images[0] || "" };
    });
  };

  const onImageDrop = (idx) => {
    reorderImage(imgDrag, idx);
    setImgDrag(null);
    setImgOver(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    const rate = CURRENCIES[priceCurrency].rate;
    const amount = parseFloat(form.price_rub) || 0;
    const originalAmount = parseFloat(form.original_price_rub) || 0;
    const isSized = form.category === "clothing" || form.category === "shoes" || (form.category === "jewellery" && ringSizing);

    let sizeStock = {};
    let sizes = [];
    let stock = parseInt(form.stock, 10) || 0;
    if (isSized) {
      Object.entries(form.size_stock || {}).forEach(([s, q]) => {
        const n = parseInt(q, 10) || 0;
        if (n > 0) { sizeStock[s] = n; sizes.push(s); }
      });
      stock = Object.values(sizeStock).reduce((a, b) => a + b, 0);
      if (sizes.length === 0) {
        toast.error("Add a stock quantity for at least one size");
        return;
      }
    }

    setSaving(true);
    const payload = {
      name: form.name,
      brand: form.brand,
      category: form.category,
      subtitle_en: form.description,
      subtitle_ru: form.description,
      price_rub: Math.round(amount / rate),
      original_price_rub: originalAmount > 0 ? Math.round(originalAmount / rate) : 0,
      image: (form.images && form.images[0]) || form.image,
      images: form.images || [],
      featured: form.featured,
      colors: form.colors,
      stock,
      sizes,
      size_stock: sizeStock,
    };
    try {
      if (editingId) {
        await updateProduct(editingId, payload);
        toast.success("Product updated");
      } else {
        await createProduct(payload);
        toast.success("Product added");
      }
      resetForm();
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
      if (err.response?.status === 401) logout();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteProduct(id);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error("Delete failed");
      if (err.response?.status === 401) logout();
    }
  };

  const handleDrop = async (targetId) => {
    const srcId = draggingId;
    setDraggingId(null);
    setDragOverId(null);
    if (!srcId || srcId === targetId) return;
    const arr = [...products];
    const from = arr.findIndex((p) => p.id === srcId);
    const to = arr.findIndex((p) => p.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setProducts(arr);
    try {
      await reorderProducts(arr.map((p) => p.id));
      toast.success("Order updated");
    } catch (err) {
      toast.error("Could not save order");
      if (err.response?.status === 401) logout();
      load();
    }
  };

  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted" data-testid="admin-loading">Loading…</div>;

  const inputCls = "mt-2 w-full bg-transparent border-b border-line py-2 font-light focus:outline-none focus:border-ink transition-colors";

  return (
    <div className="min-h-screen bg-paper px-5 md:px-10 py-10" data-testid="admin-page">
      <div className="flex items-center justify-between mb-10 border-b border-line pb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-muted mb-1">Heda Gelogaeva</p>
          <h1 className="font-display text-4xl md:text-5xl">Manage Wares</h1>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-xs uppercase tracking-[0.2em] link-underline" data-testid="admin-view-site">View site</button>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] link-underline">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="flex items-center gap-6 mb-8 border-b border-line" data-testid="admin-tabs">
        <button
          onClick={() => setTab("products")}
          data-testid="tab-products"
          className={`flex items-center gap-2 pb-3 text-xs uppercase tracking-[0.2em] -mb-px border-b-2 transition-colors ${tab === "products" ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink"}`}
        >
          <Package className="h-4 w-4" /> Products
        </button>
        <button
          onClick={() => setTab("orders")}
          data-testid="tab-orders"
          className={`flex items-center gap-2 pb-3 text-xs uppercase tracking-[0.2em] -mb-px border-b-2 transition-colors ${tab === "orders" ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink"}`}
        >
          <Receipt className="h-4 w-4" /> Orders {orders.length > 0 && <span className="text-muted">({orders.length})</span>}
        </button>
      </div>

      {tab === "products" && (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-5 border border-line p-6 md:p-8" data-testid="product-form">
          <h2 className="font-display text-2xl mb-6">{editingId ? "Edit product" : "Add a new product"}</h2>

          <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Name</label>
          <input required value={form.name} onChange={update("name")} data-testid="pf-name" className={inputCls} />

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Brand</label>
            <input value={form.brand} onChange={update("brand")} data-testid="pf-brand" className={inputCls} />
          </div>

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Category</label>
            <select value={form.category} onChange={changeCategory} data-testid="pf-category" className={inputCls}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {form.category === "jewellery" && (
            <label className="mt-5 flex items-center gap-3 cursor-pointer select-none" data-testid="pf-ring-toggle">
              <span className={`h-5 w-5 border border-ink flex items-center justify-center transition-colors ${ringSizing ? "bg-ink text-paper" : "bg-transparent"}`}>
                {ringSizing && <Plus className="h-3.5 w-3.5 rotate-45" />}
              </span>
              <input type="checkbox" checked={ringSizing} onChange={toggleRingSizing} className="sr-only" data-testid="pf-ring-checkbox" />
              <span className="text-xs uppercase tracking-[0.1em]">This is a ring — add EU ring sizes</span>
            </label>
          )}

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Description</label>
            <input value={form.description} onChange={update("description")} placeholder="White gold · Diamonds" data-testid="pf-description" className={inputCls} />
          </div>

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Selling price</label>
            <div className="flex items-end gap-3">
              <input required type="number" min="0" step="any" value={form.price_rub} onChange={update("price_rub")} placeholder="0" data-testid="pf-price" className="mt-2 flex-1 min-w-0 bg-transparent border-b border-line py-2 font-light focus:outline-none focus:border-ink transition-colors" />
              <select value={priceCurrency} onChange={(e) => setPriceCurrency(e.target.value)} data-testid="pf-currency" className="mt-2 w-24 shrink-0 bg-transparent border-b border-line py-2 font-light focus:outline-none focus:border-ink transition-colors">
                {Object.keys(CURRENCIES).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Original price <span className="text-ink/40">(optional — shows a strike-through discount)</span></label>
            <input type="number" min="0" step="any" value={form.original_price_rub} onChange={update("original_price_rub")} placeholder="0" data-testid="pf-original-price" className="mt-2 w-full bg-transparent border-b border-line py-2 font-light focus:outline-none focus:border-ink transition-colors" />
            <p className="text-[10px] text-muted mt-1">Leave blank or 0 for no discount. When higher than the selling price, the original shows crossed out and the selling price appears in red.</p>
          </div>

          {!(form.category === "clothing" || form.category === "shoes" || (form.category === "jewellery" && ringSizing)) && (
            <div className="mt-5">
              <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Stock quantity</label>
              <input required type="number" min="0" step="1" value={form.stock} onChange={update("stock")} placeholder="0" data-testid="pf-stock" className={inputCls} />
            </div>
          )}

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Available colours</label>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="pf-colors">
              {COLORS.map((c) => {
                const active = form.colors.includes(c.name);
                return (
                  <button
                    type="button"
                    key={c.name}
                    onClick={() => toggleColor(c.name)}
                    data-testid={`pf-color-${c.name.toLowerCase()}`}
                    className={`flex items-center gap-2 border pl-1.5 pr-3 py-1.5 text-xs transition-colors ${active ? "border-ink bg-ink text-paper" : "border-line text-ink hover:border-ink"}`}
                  >
                    <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: c.hex }} />
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          {(form.category === "clothing" || form.category === "shoes" || (form.category === "jewellery" && ringSizing)) && (
            <div className="mt-5">
              <label className="text-[10px] uppercase tracking-[0.22em] text-muted">
                {form.category === "shoes" ? "Shoe sizes & stock (EU)" : form.category === "jewellery" ? "Ring sizes & stock (RU)" : "Sizes & stock"}
              </label>
              <p className="text-[10px] text-muted mt-1">Add each size you stock and set its quantity. Total stock is the sum.</p>
              <div className="mt-3">
                <Select value="" onValueChange={addSize}>
                  <SelectTrigger data-testid="pf-size-add" className="w-full bg-transparent border-line text-sm">
                    <SelectValue placeholder="Add a size…" />
                  </SelectTrigger>
                  <SelectContent className="bg-paper border-line max-h-72">
                    {sizeListFor(form.category).filter((s) => form.size_stock?.[s] == null).map((s) => (
                      <SelectItem key={s} value={s} data-testid={`pf-size-opt-${s.toLowerCase()}`}>{sizePrefix(form.category)}{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sizeListFor(form.category).filter((s) => form.size_stock?.[s] != null).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-3" data-testid="pf-size-stock">
                  {sizeListFor(form.category).filter((s) => form.size_stock?.[s] != null).map((s) => (
                    <div key={s} className="flex items-center gap-2 border border-line px-3 py-2">
                      <span className="text-xs uppercase tracking-[0.1em] w-12 shrink-0">{sizePrefix(form.category)}{s}</span>
                      <input
                        type="number" min="0" step="1"
                        value={form.size_stock?.[s] ?? ""}
                        onChange={updateSizeStock(s)}
                        placeholder="0"
                        data-testid={`pf-size-stock-${s.toLowerCase()}`}
                        className="w-full min-w-0 bg-transparent border-b border-line py-1 text-sm font-light focus:outline-none focus:border-ink transition-colors"
                      />
                      <button type="button" onClick={() => removeSize(s)} data-testid={`pf-size-remove-${s.toLowerCase()}`} aria-label={`Remove ${sizePrefix(form.category)}${s}`} className="shrink-0 text-muted hover:text-red-600 transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted mt-2" data-testid="pf-size-total">
                Total stock: {Object.values(form.size_stock || {}).reduce((a, b) => a + (parseInt(b, 10) || 0), 0)}
              </p>
            </div>
          )}

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">Product photos <span className="text-ink/40">(up to 5 — first is the cover)</span></label>
            <label className={`mt-2 flex items-center justify-center gap-2 border border-dashed border-line py-4 text-xs uppercase tracking-[0.2em] cursor-pointer hover:bg-surface transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="pf-upload-label">
              <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload from device"}
              <input type="file" accept="image/*" multiple onChange={handleFile} data-testid="pf-file" className="hidden" />
            </label>
            <div className="flex gap-2 mt-2">
              <input value={form.image} onChange={update("image")} placeholder="…or paste an image URL" data-testid="pf-image" className={inputCls + " flex-1"} />
              <button type="button" onClick={addImageUrl} data-testid="pf-add-url" className="mt-2 shrink-0 border border-ink px-4 text-xs uppercase tracking-[0.16em] hover:bg-ink hover:text-paper transition-colors">Add</button>
            </div>
            {(form.images || []).length > 0 && (
              <>
                {form.images.length > 1 && (
                  <p className="text-[10px] text-muted mt-3 flex items-center gap-1.5" data-testid="pf-reorder-hint">
                    <GripVertical className="h-3 w-3" /> Drag photos to reorder — the first is the cover.
                  </p>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2" data-testid="pf-images-grid">
                  {form.images.map((img, i) => (
                    <div
                      key={i}
                      draggable
                      onDragStart={() => setImgDrag(i)}
                      onDragEnd={() => { setImgDrag(null); setImgOver(null); }}
                      onDragOver={(e) => { e.preventDefault(); if (imgOver !== i) setImgOver(i); }}
                      onDrop={() => onImageDrop(i)}
                      className={`relative group aspect-[3/4] bg-surface cursor-grab active:cursor-grabbing touch-none transition-all ${imgDrag === i ? "opacity-40" : ""} ${imgOver === i && imgDrag !== null && imgDrag !== i ? "ring-2 ring-ink ring-offset-2 ring-offset-paper" : ""}`}
                      data-testid={`pf-image-${i}`}
                    >
                      <img src={img} alt="" className="h-full w-full object-cover pointer-events-none" />
                      {i === 0 && <span className="absolute top-1 left-1 bg-ink text-paper text-[8px] uppercase tracking-[0.12em] px-1.5 py-0.5">Cover</span>}
                      <span className="absolute bottom-1 left-1 h-5 w-5 flex items-center justify-center bg-paper/85 text-ink opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" aria-hidden="true">
                        <GripVertical className="h-3.5 w-3.5" />
                      </span>
                      <button type="button" onClick={() => removeImage(i)} data-testid={`pf-remove-image-${i}`} aria-label="Remove photo"
                        className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center bg-paper/90 text-ink hover:bg-red-600 hover:text-white transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <label className="flex items-center gap-3 mt-5 cursor-pointer select-none" data-testid="pf-featured-label">
            <input type="checkbox" checked={form.featured} onChange={update("featured")} data-testid="pf-featured" />
            <span className="text-sm">Feature on home page</span>
          </label>

          <div className="flex items-center gap-3 mt-8">
            <button type="submit" disabled={saving} data-testid="pf-submit" className="flex-1 flex items-center justify-center gap-2 bg-ink text-paper py-3.5 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink disabled:opacity-60">
              <Plus className="h-4 w-4" /> {saving ? "Saving…" : editingId ? "Update" : "Add product"}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} data-testid="pf-cancel" className="px-5 py-3.5 border border-line text-xs uppercase tracking-[0.2em] hover:bg-surface">Cancel</button>
            )}
          </div>
        </form>

        {/* List */}
        <div className="lg:col-span-7">
          {(() => {
            const LIST_FILTERS = [
              { value: "all", label: "All" },
              { value: "clothing", label: "Clothes" },
              { value: "shoes", label: "Shoes" },
              { value: "bags", label: "Bags" },
              { value: "jewellery", label: "Jewellery" },
            ];
            const countFor = (v) => (v === "all" ? products.length : products.filter((p) => p.category === v).length);
            const visibleProducts = listCategory === "all" ? products : products.filter((p) => p.category === listCategory);
            return (
              <>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-category-filters">
                  {LIST_FILTERS.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setListCategory(f.value)}
                      data-testid={`admin-filter-${f.value}`}
                      className={`px-4 py-2 text-[10px] uppercase tracking-[0.18em] border transition-colors ${listCategory === f.value ? "bg-ink text-paper border-ink" : "bg-transparent text-ink border-line hover:border-ink"}`}
                    >
                      {f.label} <span className="opacity-60">({countFor(f.value)})</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs uppercase tracking-[0.22em] text-muted mb-1">{visibleProducts.length} {visibleProducts.length === 1 ? "product" : "products"}</p>
                <p className="text-[10px] text-muted mb-4">Drag the handle to reorder how pieces appear in the Catalogue.</p>
                <div className="divide-y divide-line border-y border-line" data-testid="admin-product-list">
                  {visibleProducts.map((p) => (
              <div
                key={p.id}
                draggable
                onDragStart={() => setDraggingId(p.id)}
                onDragEnd={() => { setDraggingId(null); setDragOverId(null); }}
                onDragOver={(e) => { e.preventDefault(); if (dragOverId !== p.id) setDragOverId(p.id); }}
                onDrop={() => handleDrop(p.id)}
                className={`flex items-center gap-3 py-4 transition-colors ${draggingId === p.id ? "opacity-40" : ""} ${dragOverId === p.id && draggingId && draggingId !== p.id ? "bg-surface" : ""}`}
                data-testid={`admin-product-${p.id}`}
              >
                <button type="button" aria-label="Drag to reorder" data-testid={`admin-drag-${p.id}`} className="cursor-grab active:cursor-grabbing text-muted hover:text-ink shrink-0 touch-none">
                  <GripVertical className="h-5 w-5" />
                </button>
                {p.image ? (
                  <img src={p.image} alt={p.name} className="h-16 w-14 object-cover bg-surface pointer-events-none" />
                ) : (
                  <div className="h-16 w-14 bg-surface pointer-events-none shrink-0" aria-hidden="true" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted">{p.brand} · {p.category}{p.featured ? " · featured" : ""} · {p.stock ?? 0} in stock</p>
                  <h3 className="font-display text-xl truncate">{p.name}</h3>
                  <p className="text-sm text-ink/60">{formatPrice(p.price_rub, "USD")}</p>
                </div>
                <button onClick={() => startEdit(p)} data-testid={`admin-edit-${p.id}`} className="p-2 hover:text-accent" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(p.id)} data-testid={`admin-delete-${p.id}`} className="p-2 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
                  ))}
                  {visibleProducts.length === 0 && (
                    <p className="py-10 text-center text-sm text-muted" data-testid="admin-empty-category">{listCategory === "all" ? "No products yet." : "No products in this category yet."}</p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>
      )}

      {tab === "orders" && (
        <div data-testid="orders-view">
          {/* Next order number */}
          <div className="flex items-center justify-between gap-6 border border-ink bg-ink text-paper p-6 mb-10" data-testid="next-order-card">
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-paper/60 mb-1">Next order number</p>
              <p className="text-sm text-paper/70">The number the next successful purchase will receive.</p>
            </div>
            <span className="font-display text-4xl md:text-5xl whitespace-nowrap" data-testid="next-order-number">
              {nextOrderNo ? `#${nextOrderNo}` : "—"}
            </span>
          </div>

          {/* Tax setting */}
          <div className="flex items-start justify-between gap-6 border border-line p-6 mb-10" data-testid="tax-setting">
            <div>
              <h2 className="font-display text-2xl mb-1">Automatic tax</h2>
              <p className="text-sm text-ink/60 max-w-lg">
                When on, Stripe calculates and adds tax at card checkout. You must also activate Stripe Tax in your Stripe dashboard for it to take effect after you claim your account.
              </p>
            </div>
            <button
              onClick={toggleTax}
              disabled={savingTax}
              data-testid="tax-toggle"
              role="switch"
              aria-checked={taxEnabled}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 disabled:opacity-60 ${taxEnabled ? "bg-ink" : "bg-line"}`}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-paper transition-all duration-300 ${taxEnabled ? "left-6" : "left-1"}`} />
            </button>
          </div>

          <p className="text-xs uppercase tracking-[0.22em] text-muted mb-4">{orders.length} paid orders</p>
          {orders.length === 0 ? (
            <div className="border-y border-line py-16 text-center text-ink/50" data-testid="orders-empty">
              No paid orders yet.
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.session_id} className="border border-line p-5" data-testid={`order-${o.session_id}`}>
                  <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-line">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted">
                        {o.order_number ? <span className="text-ink font-medium">#{o.order_number} · </span> : null}
                        {(o.provider || "stripe") === "paypal" ? "PayPal" : "Card"} · {fmtDate(o.created_at)}
                      </p>
                      <p className="text-sm">{o.customer_email || "—"}</p>
                    </div>
                    <span className="font-display text-2xl whitespace-nowrap">{fmtUsd(o.amount)}</span>
                  </div>
                  <div className="space-y-1.5">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-ink/70"><span className="text-muted">{it.brand}</span> {it.name || it.id}{it.size ? ` · ${it.size}` : ""}</span>
                        {it.unit_amount ? (
                          <span className="text-ink/60">×{it.quantity || 1} · {fmtUsd((it.unit_amount || 0) * (it.quantity || 1))}</span>
                        ) : (
                          <span className="text-ink/60">×{it.quantity || 1}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  {o.shipping && (
                    <div className="mt-3 pt-3 border-t border-line" data-testid={`order-shipping-${o.session_id}`}>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted mb-1">Ship to</p>
                      <p className="text-sm text-ink/70 leading-relaxed">
                        {o.shipping.full_name}<br />
                        {o.shipping.line1}{o.shipping.line2 ? `, ${o.shipping.line2}` : ""}<br />
                        {[o.shipping.postal_code, o.shipping.city].filter(Boolean).join(" ")}
                        {[o.shipping.state, o.shipping.country].filter(Boolean).length ? `, ${[o.shipping.state, o.shipping.country].filter(Boolean).join(", ")}` : ""}
                        {o.shipping.phone ? <><br />Phone: {o.shipping.phone}</> : null}
                      </p>
                      {o.shipping.courier_note ? (
                        <p className="text-sm text-ink/60 italic mt-1" data-testid={`order-note-${o.session_id}`}>Note to courier: {o.shipping.courier_note}</p>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
